import { useMemo } from 'react';
import { parse, type OperationDefinitionNode } from 'graphql';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';

import { logRQGraphqlError } from 'services/log';

import useApi from './useApi';
import { type GqlResponse } from './useGqlQuery';

export type CustomConfig = {
  queryKey?: string;
  logError?: boolean;
  origin?: string;
};

const useGqlMutation = <TData = unknown, TVariables = Record<string, unknown>>(
  query: string,
  config: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'> & CustomConfig,
) => {
  const api = useApi();

  const operationName = useMemo(() => {
    const parsedQuery = parse(query);

    return (parsedQuery.definitions?.[0] as OperationDefinitionNode | undefined)?.name?.value;
  }, [query]);

  const result = useMutation({
    mutationFn: async (variables: TVariables) => {
      try {
        const res = await api.post<unknown, GqlResponse<TData>>(`/graphql/${operationName}`, {
          query,
          variables,
          operationName,
        });

        return res.data;
      } catch (error) {
        let thrownError: unknown;
        if (axios.isAxiosError(error)) {
          // Fall back to the original error when the response carries no GraphQL
          // errors array (e.g. network failures) so callers never receive `undefined`.
          thrownError = error.response?.data?.errors ?? error;
        } else {
          thrownError = error;
        }

        if (config.logError) {
          logRQGraphqlError({
            error: error as Error,
            origin: config.origin || `${operationName} Mutation`,
            variables: variables as Record<string, unknown>,
          });
        }

        throw thrownError;
      }
    },

    ...config,
  });

  if (import.meta.env.DEV) {
    return new Proxy(result, {
      get(target, prop) {
        if (prop === 'loading') {
          throw new Error('`loading` flag is deprecated, please use `isLoading`');
        }

        return target[prop as keyof typeof target];
      },
    }) as typeof result;
  }

  return result;
};

export default useGqlMutation;
